import type {
  MobileFrontEndBubbleMessageView,
  MobileFrontEndCardView,
  MobileFrontEndGalleryView,
  MobileFrontEndMapView,
  MobileFrontEndRecordView,
  MobileFrontEndTableView,
  MobileFrontEndTimelineView,
  MobileFrontEndViewConfig,
  MobileUiConfigArtifact,
  MobileUiConfigResponse,
} from '@/shared/mobile-service/api/cors-mobile-api';
import type { ComponentType } from 'react';

/**
 * Registry of artifact presentation kinds.
 *
 * The orchestrator used to hardcode a ladder of `messages → map → table`, which
 * meant every new presentation edited the same component and grew the same
 * conditional. Here each view kind is one entry: a guard that narrows the
 * schema's discriminated union, a priority for picking the default, and the
 * component that renders it.
 *
 * Registration lives in `components/views/registry.tsx` so this module stays
 * free of JSX and remains unit-testable as pure logic, matching the module's
 * existing pure-lib / hook / component split.
 */

/** Props every artifact view component receives. */
export interface ArtifactViewProps<TView extends MobileFrontEndViewConfig> {
  acquisitionId: number;
  graphqlType: string;
  /** The narrowed schema config for this view kind. */
  view: TView;
  artifact: MobileUiConfigArtifact;
  uiConfig: MobileUiConfigResponse;
  displayName: string;
}

export interface ArtifactViewDef<TView extends MobileFrontEndViewConfig> {
  /** The view's `name` discriminator, e.g. `table`. */
  name: string;
  /** Catalog key for the View selector label. `name` stays the stable id. */
  labelKey: string;
  /** Narrows a `front_end.views[]` entry to this kind. */
  guard: (view: MobileFrontEndViewConfig) => view is TView;
  /**
   * Default-selection precedence. Higher wins, so a purpose-built presentation
   * opens ahead of the generic table when an artifact declares both.
   */
  priority: number;
  Component: ComponentType<ArtifactViewProps<TView>>;
}

// The registry is heterogeneous by construction — each entry is keyed to its own
// view type — so it is stored under a widened element type. `resolveArtifactView`
// re-establishes the correlation between a def and its narrowed config.
// eslint-disable-next-line @typescript-eslint/no-explicit-any
export type AnyArtifactViewDef = ArtifactViewDef<any>;

const registry = new Map<string, AnyArtifactViewDef>();

/** Register a view kind. Later registrations replace earlier ones by `name`. */
export function registerArtifactView<TView extends MobileFrontEndViewConfig>(
  def: ArtifactViewDef<TView>,
): void {
  registry.set(def.name, def);
}

export function getArtifactViewDef(name: string): AnyArtifactViewDef | undefined {
  return registry.get(name);
}

/** Type guards used by the registry entries, kept here so `labels.ts` can reuse them. */
export function isTableView(view: MobileFrontEndViewConfig): view is MobileFrontEndTableView {
  return view.name === 'table';
}
export function isBubbleView(
  view: MobileFrontEndViewConfig,
): view is MobileFrontEndBubbleMessageView {
  return view.name === 'messages';
}
export function isMapView(view: MobileFrontEndViewConfig): view is MobileFrontEndMapView {
  return view.name === 'map';
}
export function isGalleryView(
  view: MobileFrontEndViewConfig,
): view is MobileFrontEndGalleryView {
  return view.name === 'gallery' && 'media_field' in view && Boolean(view.media_field);
}
export function isTimelineView(
  view: MobileFrontEndViewConfig,
): view is MobileFrontEndTimelineView {
  return view.name === 'timeline' && 'timestamp_field' in view && Boolean(view.timestamp_field);
}
export function isCardView(view: MobileFrontEndViewConfig): view is MobileFrontEndCardView {
  return view.name === 'cards' && 'title_field' in view && Boolean(view.title_field);
}
/** `record` is a docked panel rather than a center view, so it has no registry entry. */
export function isRecordView(
  view: MobileFrontEndViewConfig,
): view is MobileFrontEndRecordView {
  return view.name === 'record';
}

/** A view the artifact declares that the viewer knows how to render. */
export interface AvailableArtifactView {
  def: AnyArtifactViewDef;
  config: MobileFrontEndViewConfig;
}

/**
 * The artifact's declared views that this build can actually render, in
 * default-selection order.
 *
 * Views the schema declares but the frontend hasn't implemented are omitted
 * rather than offered and then failing — the schema ships ahead of the viewer by
 * design, so an unknown `name` is expected, not exceptional. A view whose guard
 * rejects its own config is also dropped, which catches a malformed block (a
 * gallery with no `media_field`, say) before it reaches a component.
 *
 * An artifact with no usable view still gets a table, since column resolution
 * falls back to the introspected scalar fields. Dozens of types have no curated
 * `front_end` block and must stay browsable.
 */
export function availableArtifactViews(
  artifact: MobileUiConfigArtifact | undefined,
): AvailableArtifactView[] {
  if (!artifact) return [];
  const available: AvailableArtifactView[] = [];
  for (const config of artifact.front_end?.views ?? []) {
    const def = registry.get(config.name);
    if (!def || !def.guard(config)) continue;
    available.push({ def, config });
  }
  if (!available.length) {
    const tableDef = registry.get('table');
    if (tableDef) available.push({ def: tableDef, config: { name: 'table' } });
  }
  return available.sort((a, b) => b.def.priority - a.def.priority);
}

/**
 * Resolve which view to render.
 *
 * @param requested - An explicitly selected view name (from the URL), or null to
 *   take the artifact's highest-priority view.
 */
export function resolveArtifactView(
  artifact: MobileUiConfigArtifact | undefined,
  requested: string | null,
): AvailableArtifactView | null {
  const available = availableArtifactViews(artifact);
  if (!available.length) return null;
  if (requested) {
    const match = available.find((entry) => entry.def.name === requested);
    if (match) return match;
  }
  return available[0];
}
